import React, { useState } from "react";
import sectionImg from "../../images/Section 06.webp";
import "../../styles/main.scss";
import CaseStudiesModal from "../common/CaseStudiesModal";

const painCards = [
  {
    num: "01",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
        <line x1="16" x2="16" y1="2" y2="6" />
        <line x1="8" x2="8" y1="2" y2="6" />
        <line x1="3" x2="21" y1="10" y2="10" />
        <path d="M8 14h.01" />
        <path d="M12 14h.01" />
        <path d="M16 14h.01" />
        <path d="M8 18h.01" />
        <path d="M12 18h.01" />
        <path d="M16 18h.01" />
      </svg>
    ),
    title: "Busy Month",
    desc: "The salon looked active, the effort was real, and the business kept moving — but the profit that finally stayed in hand still did not feel equal to that effort.",
  },
  {
    num: "02",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
    title: "Late Truth",
    desc: "The real pain was not only low profit. The real pain was seeing the truth after it was already too late. Month-end truth always comes late.",
  },
  {
    num: "03",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
        <path d="M3 3v5h5" />
      </svg>
    ),
    title: "The Repeating Cycle",
    desc: "Busy month. Late understanding. No timely control. Same month-end feeling.",
  },
  {
    num: "04",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3v18h18" />
        <path d="m19 9-5 5-4-4-3 3" />
      </svg>
    ),
    title: "What Changed with Munafa Engine",
    desc: "Once the salon's performance started becoming visible week by week, the owner no longer had to wait till month-end to understand what had been happening.",
  },
];

const seeingPoints = [
  "What the salon had actually brought in",
  "What had gone out",
  "What had really remained",
  "Where pressure was building",
  "Where money may be slipping",
  "And whether the week had actually gone well, or had only looked busy from the outside",
];

const Section06 = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <section className="section-06">
      <div className="section-06__hero">
        <img
          src={sectionImg}
          alt="Salon owner reviewing month-end numbers"
          className="section-06__hero-image"
         loading="lazy" decoding="async"/>

        <div className="section-06__hero-overlay" />

        <div className="section-06__hero-content">
          <h2 className="section-06__title">
            When a Busy Salon Still Leaves the Owner{" "}
            <span>Disappointed at Month-End</span>
          </h2>

          <p className="section-06__intro">
            Many salon owners know this feeling. The salon stays busy. Clients keep coming.
            Staff keeps working. The chairs stay occupied. From the outside, everything looks active.
            And because everything looks active, the owner also keeps feeling:
            <em> "This month should end well."</em>
          </p>

          <div className="section-06__quote-box">
            <div className="section-06__quote-mark">"</div>

            <p>
              The salon stayed busy all month...
              <br />
              then why does the final profit still feel so low?
            </p>
          </div>

          <div className="section-06__example-box">
            <div className="section-06__example-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>

            <div>
              <h3>Anonymised Salon Example</h3>

              <button
                onClick={() => setIsModalOpen(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#f59e0b',
                  fontSize: '15px',
                  fontWeight: '600',
                  padding: '0',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  marginTop: '8px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                onMouseOver={(e) => e.currentTarget.style.color = '#d97706'}
                onMouseOut={(e) => e.currentTarget.style.color = '#f59e0b'}
              >
                Read Full Case Study →
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="section-06__body">
        <div className="section-06__cards">
          {painCards.map((card) => (
            <article className="section-06__card" key={card.num}>
              <div className="section-06__card-top">
                <div className="section-06__card-icon">{card.icon}</div>

                <div className="section-06__card-num">{card.num}</div>
              </div>

              <h3>{card.title}</h3>

              <div className="section-06__card-line" />

              <p>{card.desc}</p>
            </article>
          ))}
        </div>

        <aside className="section-06__seeing-card">
          <h3>Now, the owner could start seeing:</h3>

          <ul>
            {seeingPoints.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        </aside>
      </div>

      <CaseStudiesModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </section>
  );
};

export default Section06;